import { z } from 'zod';
/**
 * The supported locale set. This lives in `contracts`, not in `packages/i18n`,
 * because `contracts` is the only package both the browser and the server import
 * and it must stay dependency-free. `packages/i18n` imports LOCALES from here —
 * never the reverse. See docs/011-i18n.md.
 */
export const LOCALES = ['en', 'es', 'fr'];
/** `en` is the source of truth and the terminal fallback. */
export const DEFAULT_LOCALE = 'en';
export const localeCodeSchema = z.enum(LOCALES);
/**
 * NOTE: docs/011 specifies `z.partialRecord(z.enum(LOCALES), z.string())`, which is a
 * Zod 4 API. The stack is frozen at Zod 3 (docs/003), where `z.record` with an enum key
 * produces a *required* key for every enum member. The object form below is the Zod 3
 * equivalent and yields exactly the `MultiLanguageContent` type above.
 */
export const multiLanguageContentSchema = z
    .object({
    en: z.string(),
    es: z.string().optional(),
    fr: z.string().optional(),
})
    .refine((value) => Boolean(value.en.trim()), { message: 'EN_CONTENT_REQUIRED' });
export function isLocaleCode(value) {
    return LOCALES.includes(value);
}
/**
 * `multiLanguageContentSchema` with a per-locale length ceiling.
 *
 * ADMIN INPUT ONLY. A length bound constrains what an admin MAY SAVE; it must never
 * constrain what the server WILL SEND — a bound on an outbound schema turns one row that
 * predates the ceiling into a payload nobody can parse (see `storedContentSchema` in
 * ./check-in-flow.ts). Walks `Object.entries` of the already-parsed value, so it never
 * names a locale and needs no edit when `LOCALES` grows.
 */
export const boundedMultiLanguageContentSchema = (maxCharsPerLocale) => multiLanguageContentSchema.superRefine((value, ctx) => {
    for (const [locale, text] of Object.entries(value)) {
        if (typeof text === 'string' && text.length > maxCharsPerLocale) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [locale], message: 'CONTENT_TOO_LONG' });
        }
    }
});
//# sourceMappingURL=i18n.js.map